import React, { useEffect } from 'react';

interface WhatIsGrandLineModalProps {
  onClose: () => void;
}

export const WhatIsGrandLineModal: React.FC<WhatIsGrandLineModalProps> = ({ onClose }): React.ReactElement => {
  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        fontFamily: '"Sometype Mono", monospace',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#0d1117',
          border: '2px solid #21262d',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
          padding: '24px',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, color: '#e2e8f0', fontSize: '18px' }}>
            What is <span style={{ color: '#58a6ff', fontFamily: 'var(--font-lookout)', fontSize: '24px', letterSpacing: '0.08em' }}>GRAND Online</span>?
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#8b949e',
              cursor: 'pointer',
              fontSize: '20px',
              padding: '0 4px',
            }}
          >
            X
          </button>
        </div>

        {/* Content */}
        <div style={{ color: '#c9d1d9', fontSize: '13px', lineHeight: '1.6' }}>
          <section style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#58a6ff', fontSize: '14px', marginBottom: '8px', fontFamily: 'Seurat, sans-serif' }}>THE WORLD</h3>
            <p style={{ margin: 0, fontSize: '12px' }}>
              GRAND Online is a player-driven sandbox MMO set in the Grand Latitude—a vast archipelago shaped by the ancient Riftfall. Navigate six regions from peaceful starter waters to elite pirate strongholds. Claim islands, engage in faction warfare, explore procedurally-generated dungeons (Sanctums), and uncover the mysteries of a world built on the ruins of five lost civilizations.
            </p>
          </section>

          <section style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#58a6ff', fontSize: '14px', marginBottom: '8px', fontFamily: 'Seurat, sans-serif' }}>CORE GAMEPLAY</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
              <div><strong style={{ color: '#e74c3c' }}>Naval Combat</strong> - Real-time cannon battles with range, reload, and positioning mechanics</div>
              <div><strong style={{ color: '#f39c12' }}>Cannon Focus</strong> - Precision minigame for critical hits and bonus damage</div>
              <div><strong style={{ color: '#9b59b6' }}>Boarding Combat</strong> - Dice-roll melee when ships collide, influenced by crew and skills</div>
              <div><strong style={{ color: '#3b82f6' }}>Signature Moves</strong> - Unlockable special abilities with cooldowns and stamina costs</div>
              <div><strong style={{ color: '#1abc9c' }}>Resource Harvesting</strong> - Mine nodes, fish, and gather materials for crafting</div>
              <div><strong style={{ color: '#f0883e' }}>Cooking & Crafting</strong> - Create consumables, ship upgrades, and equipment</div>
            </div>
          </section>

          <section style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#58a6ff', fontSize: '14px', marginBottom: '8px', fontFamily: 'Seurat, sans-serif' }}>PROGRESSION</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
              <div><strong style={{ color: '#58a6ff' }}>Leveling & Skills</strong> - Gain XP from combat, fishing, and quests. Unlock skill trees for combat, sailing, and survival</div>
              <div><strong style={{ color: '#58a6ff' }}>Ship Upgrades</strong> - Repair, enhance, and customize your vessel with modules and cosmetics</div>
              <div><strong style={{ color: '#58a6ff' }}>Faction Reputation</strong> - Build standing with Navy, Pirates, and Merchants for unique rewards</div>
              <div><strong style={{ color: '#58a6ff' }}>Essence & Abilities</strong> - Unlock powerful signature moves by collecting essence from defeated enemies</div>
            </div>
          </section>

          <section style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#58a6ff', fontSize: '14px', marginBottom: '8px', fontFamily: 'Seurat, sans-serif' }}>WORLD FEATURES</h3>
            <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px' }}>
              <li><strong>Island Claiming</strong> - Own islands, build structures, and defend against raids</li>
              <li><strong>Sanctums</strong> - Procedurally-generated dungeons with unique layouts, enemies, and loot</li>
              <li><strong>Rift Storms</strong> - Dangerous anomalies with 2.5-12x loot multipliers and corrupted enemies</li>
              <li><strong>Sea Monsters</strong> - Roaming creatures that spawn valuable loot and hunting opportunities</li>
              <li><strong>Pirate Raids</strong> - Cooperative or competitive island invasions with faction consequences</li>
              <li><strong>Whirlpools</strong> - Instanced pocket dimensions with unique hazards and rewards</li>
              <li><strong>Dynamic Weather</strong> - Fog, storms, and calm seas affect visibility, speed, and fishing</li>
              <li><strong>Faction Warfare</strong> - Territory control, missions, and large-scale player conflicts</li>
              <li><strong>Crew System</strong> - Recruit NPCs to improve combat effectiveness and ship capabilities</li>
              <li><strong>Trading Posts & Shipyards</strong> - Safe zones for commerce, repairs, and ship customization</li>
            </ul>
          </section>

          <section style={{ marginBottom: '20px' }}>
            <h3 style={{ color: '#58a6ff', fontSize: '14px', marginBottom: '8px', fontFamily: 'Seurat, sans-serif' }}>FACTIONS</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '12px' }}>
              <div><strong style={{ color: '#3498db' }}>Navy</strong> - Regime military, patrol and control waters</div>
              <div><strong style={{ color: '#e74c3c' }}>Crimson Corsairs</strong> - Pirate faction, raid and conquer</div>
              <div><strong style={{ color: '#9b59b6' }}>Stormbreaker Syndicate</strong> - Pirate faction, mercenary operations</div>
              <div><strong style={{ color: '#c0392b' }}>Obsidian Reavers</strong> - Pirate faction, chaos and destruction</div>
              <div><strong style={{ color: '#f39c12' }}>Merchant Guild</strong> - Trade routes, commerce, and economy</div>
            </div>
          </section>

          <section>
            <h3 style={{ color: '#58a6ff', fontSize: '14px', marginBottom: '8px', fontFamily: 'Seurat, sans-serif' }}>GET STARTED</h3>
            <p style={{ margin: 0, fontSize: '12px' }}>
              Create an account to save your progress. Start in the Eastern Reach to master the basics—combat, sailing, and resource gathering. As you grow stronger, venture into more dangerous waters, claim islands, join factions, and write your legend across the Grand Latitude.
            </p>
          </section>
        </div>

        {/* Footer */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              background: '#238636',
              border: '1px solid #2ea043',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            GOT IT
          </button>
        </div>
      </div>
    </div>
  );
};
